import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon, Dumbbell } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/coaching", label: "Coaching" },
  { to: "/pricing", label: "Pricing" },
  { to: "/transformations", label: "Results" },
  { to: "/blog", label: "Blog" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar({ theme, toggleTheme }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3 backdrop-blur-xl bg-background/70 border-b border-border" : "py-5 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="w-9 h-9 rounded-full bg-accent flex items-center justify-center">
            <Dumbbell className="w-5 h-5 text-white" />
          </span>
          <span className="font-heading font-black text-lg tracking-tight uppercase">
            Manav<span className="text-accent">Fitcoach</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium link-underline transition-colors ${
                  isActive ? "text-accent" : "text-foreground/80 hover:text-foreground"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className="w-10 h-10 rounded-full glass flex items-center justify-center hover:text-accent transition-colors"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <Link
            to="/coaching"
            data-testid="nav-apply-btn"
            className="hidden md:inline-flex items-center rounded-full bg-accent text-white px-6 py-2.5 text-sm font-semibold hover:scale-105 transition-transform"
          >
            Apply Now
          </Link>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className="lg:hidden w-10 h-10 rounded-full glass flex items-center justify-center"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-background/95 border-t border-border mt-3"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="text-lg font-heading font-bold uppercase tracking-tight">
                  {l.label}
                </NavLink>
              ))}
              <Link to="/coaching" className="rounded-full bg-accent text-white px-6 py-3 text-center font-semibold mt-2">
                Apply Now
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
